import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { getMainPrice, PriceWithProduct } from '@/services/prices'
import { Check, Sparkles } from 'lucide-react'

const formatPrice = (price: PriceWithProduct | null): string => {
  if (!price || typeof price.unit_amount !== 'number') {
    return ''
  }
  const amount = price.unit_amount / 100
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount)
}

const formatInterval = (price: PriceWithProduct | null): string => {
  if (!price || !price.interval) {
    return ''
  }
  if (price.interval === 'month') {
    return '/mês'
  }
  if (price.interval === 'year') {
    return '/ano'
  }
  return `a cada ${price.interval_count} ${price.interval}`
}

export const PricingSection = () => {
  const [price, setPrice] = useState<PriceWithProduct | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const mainPrice = await getMainPrice()
        setPrice(mainPrice)
      } catch (err) {
        setError(
          'Não foi possível carregar o preço. Por favor, entre em contato conosco.',
        )
        console.error(err)
      } finally {
        setIsLoading(false)
      }
    }
    fetchPrice()
  }, [])

  if (isLoading) {
    return (
      <section className="py-20 md:py-32 bg-background">
        <div className="container mx-auto px-4 flex flex-col items-center">
          <Skeleton className="h-10 w-3/4 mb-4" />
          <Skeleton className="h-6 w-1/2 mb-12" />
          <Card className="w-full max-w-md">
            <CardHeader>
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-4 w-2/3 mt-2" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-10 w-1/2 mx-auto" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
            </CardContent>
            <CardFooter>
              <Skeleton className="h-12 w-full" />
            </CardFooter>
          </Card>
        </div>
      </section>
    )
  }

  return (
    <section id="pricing" className="py-20 md:py-32">
      <div className="container mx-auto px-4 flex flex-col items-center">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
          Preço Simples e Transparente
        </h2>
        <p className="text-lg text-muted-foreground text-center max-w-2xl mx-auto mb-12">
          Comece com 7 dias grátis. Cancele a qualquer momento.
        </p>

        {error || !price ? (
          <Card className="w-full max-w-md text-center p-8 bg-card">
            <CardTitle>Preço Sob Consulta</CardTitle>
            <CardContent>
              <p className="text-muted-foreground mt-4">
                {error ||
                  'Por favor, entre em contato para mais informações sobre preços.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="w-full max-w-md shadow-2xl shadow-primary/10 border-2 border-primary/50 animate-fade-in-up bg-card">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">
                {price.products?.name || 'Plano Mensal'}
              </CardTitle>
              <CardDescription>
                Acesso ilimitado a todos os recursos premium.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center">
                <span className="text-5xl font-bold">{formatPrice(price)}</span>
                <span className="text-xl text-muted-foreground">
                  {formatInterval(price)}
                </span>
              </div>
              {price.trial_period_days && (
                <div className="text-center font-semibold text-green-400 bg-green-500/10 py-2 rounded-md">
                  <p>Comece com {price.trial_period_days} dias grátis!</p>
                </div>
              )}
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-center">
                  <Check className="mr-3 h-5 w-5 text-primary" />
                  Até 10 Feeds RSS
                </li>
                <li className="flex items-center">
                  <Check className="mr-3 h-5 w-5 text-primary" />
                  Até 5 E-mails destinatários
                </li>
                <li className="flex items-center">
                  <Check className="mr-3 h-5 w-5 text-primary" />
                  Tópicos Prioritários
                </li>
                <li className="flex items-center">
                  <Check className="mr-3 h-5 w-5 text-primary" />
                  Envio Diário Automatizado
                </li>
              </ul>
            </CardContent>
            <CardFooter>
              <Button className="w-full" size="lg" asChild>
                <Link to="/signup">
                  <Sparkles className="mr-2 h-4 w-4" />
                  Iniciar teste gratuito
                </Link>
              </Button>
            </CardFooter>
          </Card>
        )}
      </div>
    </section>
  )
}
